import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import './Login.css';

function Login() {
  const [step, setStep] = useState('email'); // 'email' or 'verification'
  const [email, setEmail] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [serviceDown, setServiceDown] = useState(false);

  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Flash message priority: explicit router state (e.g. from CreateAccount),
  // then the session-expired flag set by apiService on any 401.
  const routerFlash = location.state?.flash ?? null;
  const expiredFlash = (() => {
    if (sessionStorage.getItem('session_expired')) {
      sessionStorage.removeItem('session_expired');
      return 'Your session has expired. Please sign in again.';
    }
    return null;
  })();
  const flashMessage = routerFlash ?? expiredFlash;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await authService.login(email);
      setStep('verification');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...codeDigits];
    newDigits[index] = value.slice(-1);
    setCodeDigits(newDigits);
    setError('');

    if (value && index < 5) {
      const nextInput = document.getElementById(`login-code-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const prevInput = document.getElementById(`login-code-digit-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const fullCode = codeDigits.join('');
    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits');
      setIsLoading(false);
      return;
    }

    try {
      const data = await authService.verifyLogin(email, fullCode);
      if (data.user) setSession(data.user);
      navigate('/dashboard');
    } catch (err) {
      if (err.serviceUnavailable) {
        setServiceDown(true);
      } else {
        setError(err.message || 'Invalid verification code. Please try again.');
      }
      setCodeDigits(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCodeDigits(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {serviceDown ? (
          <>
            <div className="login-header">
              <h1>Service Unavailable</h1>
              <p>We couldn't reach the login service. Please try again in a moment.</p>
            </div>
            <div className="login-form">
              <button
                className="login-button"
                onClick={() => { setServiceDown(false); setStep('email'); }}
              >
                Try Again
              </button>
            </div>
          </>
        ) : step === 'email' ? (
          <>
            <div className="login-header">
              <h1>Welcome Back</h1>
              <p>Sign in to your Herding Helpers account</p>
            </div>

            <form onSubmit={handleEmailSubmit} className="login-form">
              {flashMessage && <div className="flash-message">{flashMessage}</div>}
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Sending Code...' : 'Send Verification Code'}
              </button>
            </form>

            <div className="login-footer">
              <p>Don't have an account? <Link to="/create-account">Create one here</Link></p>
            </div>
          </>
        ) : (
          <>
            <div className="login-header">
              <h1>Verify Your Email</h1>
              <p>We've sent a 6-digit code to {email}</p>
            </div>

            <form onSubmit={handleVerificationSubmit} className="login-form">
              {error && <div className="error-message">{error}</div>}

              <div className="code-input-group">
                {codeDigits.map((digit, index) => (
                  <input
                    key={index}
                    id={`login-code-digit-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="code-digit-input"
                    placeholder="0"
                    inputMode="numeric"
                  />
                ))}
              </div>

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>

            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
